import React, { useState } from "react";
import { Heart } from "lucide-react";
import { useWishlist } from "../../lib/useWishlist";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";

/**
 * WishlistButton — bouton cœur réutilisable.
 * Props:
 *  - productId (required)
 *  - variant: "icon" (default, bouton circulaire) | "pill" (texte + icône)
 *  - className: extra classes
 *  - size: "sm" | "md" | "lg"
 */
export default function WishlistButton({ productId, variant = "icon", size = "md", className = "" }) {
  const { settings } = useSettings();
  const { customer } = useCustomerAuth();
  const { isFavorite, toggle } = useWishlist();
  const [busy, setBusy] = useState(false);

  if (!settings?.wishlist_enabled) return null;

  const on = isFavorite(productId);
  const sizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };
  const iconSizes = { sm: "w-4 h-4", md: "w-[18px] h-[18px]", lg: "w-5 h-5" };

  const handle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const fav = await toggle(productId);
      if (fav && !customer) {
        toast.success("Ajouté à vos favoris (connectez-vous pour les synchroniser)");
      } else if (fav) {
        toast.success("Ajouté à vos favoris");
      } else {
        toast.success("Retiré de vos favoris");
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Connectez-vous pour ajouter aux favoris");
      } else {
        toast.error(err?.response?.data?.detail || "Erreur");
      }
    } finally { setBusy(false); }
  };

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        className={`inline-flex items-center gap-2 px-4 h-10 rounded-full font-semibold text-sm border transition-all ${
          on ? "bg-[color:var(--brand-red)] text-white border-[color:var(--brand-red)]" : "bg-white text-[color:var(--brand-navy)] border-[hsl(var(--border))] hover:border-[color:var(--brand-red)]/60"
        } ${className}`}
        data-testid={`wishlist-pill-${productId}`}
      >
        <Heart className={`${iconSizes[size]} ${on ? "fill-current" : ""}`} />
        {on ? "Dans vos favoris" : "Ajouter aux favoris"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      className={`${sizes[size]} inline-flex items-center justify-center rounded-full transition-all shadow-sm hover:shadow-md ${
        on ? "bg-[color:var(--brand-red)] text-white" : "bg-white/95 text-[color:var(--brand-navy)] hover:text-[color:var(--brand-red)]"
      } ${busy ? "opacity-50" : ""} ${className}`}
      title={on ? "Retirer des favoris" : "Ajouter aux favoris"}
      aria-label={on ? "Retirer des favoris" : "Ajouter aux favoris"}
      data-testid={`wishlist-btn-${productId}`}
    >
      <Heart className={`${iconSizes[size]} transition-transform ${on ? "fill-current scale-110" : ""}`} />
    </button>
  );
}
